<template>
  <slot v-if="!error" />
  <main v-else class="error-boundary" role="alert" aria-live="assertive">
    <div class="error-illustration" aria-hidden="true"><span /><span /><span /></div>
    <p class="eyebrow">Something went wrong</p>
    <h1>This screen could not be displayed</h1>
    <p>Try loading the screen again. Your saved account and event data have not been changed.</p>
    <div class="error-actions">
      <IonButton @click="reset">Try again</IonButton>
      <IonButton fill="outline" router-link="/events" @click="reset">Go to Events</IonButton>
    </div>
  </main>
</template>

<script setup lang="ts">
import { onErrorCaptured, ref } from 'vue';
import { IonButton } from '@ionic/vue';

const error = ref<Error | null>(null);

onErrorCaptured((captured) => {
  error.value = captured instanceof Error ? captured : new Error(String(captured));
  console.error('Component error captured by AppErrorBoundary', captured);
  return false;
});

function reset() {
  error.value = null;
}
</script>

<style scoped>
.error-boundary {
  display: flex;
  min-height: 100dvh;
  max-width: 32rem;
  margin: 0 auto;
  padding: var(--app-space-7) var(--app-space-5);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--app-muted);
  text-align: center;
}
.error-illustration {
  display: flex;
  width: 5rem;
  height: 5rem;
  align-items: end;
  justify-content: center;
  gap: 0.35rem;
  padding: 1rem;
  border-radius: var(--app-radius-lg);
  background: var(--app-danger-soft);
}
.error-illustration span {
  width: 0.65rem;
  border-radius: 0.3rem;
  background: var(--ion-color-danger);
}
.error-illustration span:nth-child(1) {
  height: 55%;
}
.error-illustration span:nth-child(2) {
  height: 90%;
}
.error-illustration span:nth-child(3) {
  height: 35%;
}
.eyebrow {
  margin: 1.25rem 0 0.35rem;
  color: var(--ion-color-danger);
  font-size: 0.75rem;
  font-weight: 750;
}
h1 {
  margin: 0;
  color: var(--app-ink);
  font-size: clamp(1.65rem, 7vw, 2.4rem);
  letter-spacing: -0.04em;
}
p:not(.eyebrow) {
  max-width: 36ch;
  line-height: 1.6;
}
.error-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--app-space-2);
  margin-top: var(--app-space-3);
}
</style>
